import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import content from "../content/content.json";

const KeepExploring = () => {
  const scrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const { keepExploring, shared } = content;

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      const handleResize = () => {
        setMaxScroll(container.scrollWidth - container.clientWidth);
        setScrollPosition(container.scrollLeft);
      };

      handleResize();

      const onScroll = () => {
        setScrollPosition(container.scrollLeft);
      };

      container.addEventListener("scroll", onScroll);
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        container.removeEventListener("scroll", onScroll);
      };
    }
  }, []);

  const scroll = (direction) => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = 400;
      const newScroll =
        direction === "left"
          ? Math.max(container.scrollLeft - scrollAmount, 0)
          : Math.min(container.scrollLeft + scrollAmount, maxScroll);
      container.scrollTo({ left: newScroll, behavior: "smooth" });
      setScrollPosition(newScroll);
    }
  };

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < maxScroll;

  return (
    <section className="w-full py-20 px-6 md:px-16 lg:px-24">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-10">
        <div className="lg:w-1/3 flex flex-col justify-between">
          <div>
            <h2 className="text-5xl font-extrabold text-[#1C1F2B] mb-6">{keepExploring.heading}</h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">{keepExploring.description}</p>
            <Link to="/insights" className="more-insight-btn group relative overflow-hidden border border-[#E4002B] px-8 py-4 font-semibold rounded-none inline-flex items-center gap-2">
              <span className="relative z-10 text-white group-hover:text-[#E4002B] transition-colors duration-300">
                {keepExploring.viewAllInsights}
              </span>
              <span className="relative z-10 text-white text-xl group-hover:text-[#E4002B] transition-colors duration-300">
                {shared.arrow}
              </span>
            </Link>
          </div>
        </div>

        <div className="lg:w-2/3 flex flex-col">
          <div className="overflow-x-auto no-scrollbar" ref={scrollRef} style={{ scrollBehavior: "smooth" }}>
            <div className="flex gap-8 min-w-max pb-4">
              {keepExploring.items.map((item) => (
                <div key={item.id} className="w-[320px] flex-shrink-0">
                  <div className="h-48 w-full overflow-hidden rounded-md mb-4">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <div>
                    <p className="text-[#007681] font-semibold text-sm uppercase mb-1">
                      {item.type}{" "}
                      <span className="text-gray-400 font-normal">/ {item.time}</span>
                    </p>
                    <h3 className="text-lg font-bold text-[#1C1F2B] leading-snug hover:underline cursor-pointer">
                      {item.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center gap-6">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className={`w-14 h-14 flex items-center justify-center border transition
                ${canScrollLeft
                  ? "bg-[#D00] text-white border-[#D00] hover:bg-[#a00] cursor-pointer"
                  : "bg-gray-300 text-gray-400 border-gray-300 cursor-not-allowed"}`}
              aria-label={keepExploring.scrollLeftAria}
            >
              <span className="text-2xl font-light">{shared.leftArrow}</span>
            </button>

            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className={`w-14 h-14 flex items-center justify-center transition
                ${canScrollRight
                  ? "bg-[#D00] text-white hover:bg-[#a00] cursor-pointer"
                  : "bg-gray-300 text-gray-400 cursor-not-allowed"}`}
              aria-label={keepExploring.scrollRightAria}
            >
              <span className="text-2xl font-light">{shared.rightArrow}</span>
            </button>

            <div className="flex-1 h-[2px] bg-gray-300 relative rounded">
              <div
                className="absolute top-0 left-0 h-[2px] bg-[#D00] transition-all duration-500 rounded"
                style={{
                  width: maxScroll > 0 ? `${(scrollPosition / maxScroll) * 100}%` : "0%",
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KeepExploring;

